import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { passwordResetRedirectUrl } from "../lib/passwordRecovery";
import { isValidEmail } from "../lib/passwordPolicy";
import {
  COOLDOWN_KEYS,
  readCooldown,
  startCooldown,
} from "../lib/resendCooldown";
import { reportError } from "../lib/errorReporter";

const KEY = COOLDOWN_KEYS.forgotPassword;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(() => readCooldown(KEY));
  const inFlight = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown(readCooldown(KEY)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      // Synchronous duplicate-submit guard: a second submit can never start a
      // second request, however fast the button is pressed.
      if (inFlight.current || busy || cooldown > 0) return;
      if (!isValidEmail(email)) {
        setError(t("password.invalid_email"));
        return;
      }
      inFlight.current = true;
      setBusy(true);
      setError("");
      const { error: requestError } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        { redirectTo: passwordResetRedirectUrl() },
      );
      if (requestError) {
        // Deliberately NOT surfaced: the outcome must be indistinguishable so
        // the screen can never reveal whether an account exists. The raw
        // provider error is never rendered and never logged.
        reportError("auth.password_reset_request");
      }
      inFlight.current = false;
      setBusy(false);
      startCooldown(KEY);
      setCooldown(readCooldown(KEY));
      setSent(true);
    },
    [busy, cooldown, email, t],
  );

  const disabled = busy || cooldown > 0;

  return (
    <div className="qw-auth-wrap">
      <div className="qw-auth-side">
        <div>
          <div className="brand">OFFERTRACE</div>
          <div className="head">{t("password.forgot_title")}</div>
          <div className="sub">{t("password.forgot_side_note")}</div>
        </div>
        <div style={{ opacity: 0.7, fontSize: 12 }}>© OfferTrace</div>
      </div>
      <div className="qw-auth-panel">
        <form
          className="qw-auth-form"
          onSubmit={submit}
          noValidate
          data-testid="forgot-password-form"
        >
          <h1>{t("password.forgot_title")}</h1>
          <p className="lead">{t("password.forgot_lead")}</p>

          <div className="qw-field">
            <label htmlFor="forgot-email">{t("auth.email")}</label>
            <input
              id="forgot-email"
              className="qw-input"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              autoComplete="email"
              aria-invalid={error ? "true" : undefined}
              data-testid="forgot-password-email"
            />
          </div>

          {error && (
            <div className="qw-error" data-testid="forgot-password-error">
              {error}
            </div>
          )}

          {sent && (
            <div
              className="qw-card"
              style={{
                background: "#ecfdf5",
                borderColor: "#a7f3d0",
                color: "#065f46",
                marginBottom: 14,
              }}
              role="status"
              data-testid="forgot-password-neutral-notice"
            >
              {t("password.neutral_sent")}
            </div>
          )}

          <button
            className="qw-btn primary stretch"
            style={{ width: "100%", justifyContent: "center" }}
            type="submit"
            disabled={disabled}
            data-testid="forgot-password-submit"
          >
            {busy ? t("password.sending") : t("password.send_link")}
          </button>

          {cooldown > 0 && (
            <div className="qw-help" data-testid="forgot-password-cooldown">
              {t("password.cooldown", { seconds: cooldown })}
            </div>
          )}

          <div style={{ marginTop: 16, color: "#64748b", fontSize: 13 }}>
            <Link
              to="/login"
              data-testid="forgot-password-back-to-login"
              style={{ color: "#0f766e", fontWeight: 600 }}
            >
              {t("password.back_to_login")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
